import type {
  ApiResponseGetProductsResponse,
  ApiResponseProductEntity,
  ApiResponseProductDetailResponse,
  ApiResponseProductCategoryEntity,
  ApiResponseProductCategoryEntityList,
  CreateProductRequest,
  UpdateProductRequest,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  LinkProductOptionsRequest, ProductManagementDto, ApiResponse, ProductDto, DetailPageDto
} from "../../../shared/types/api";
import { httpClient } from "../http-client";
import { NEW_API_ENDPOINTS } from "../../../shared/constants/api";

export class CompanyProductApiService {
  async getProducts(companyId: number): Promise<ApiResponse<ProductDto[] | ProductManagementDto>> {
    const response = await httpClient.get<ApiResponse<ProductDto[] | ProductManagementDto>>(
      NEW_API_ENDPOINTS.companyProductController.GET(companyId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상품들을 가져오지 못했습니다.");
    }

    return response.data;
  }

  async getProduct(companyId: number, productId: number): Promise<ApiResponse<ProductDto>> {
    const response = await httpClient.get<ApiResponse<ProductDto>>(
      NEW_API_ENDPOINTS.companyProductController.GET_DETAIL(companyId, productId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상품 상세 정보를 가져오지 못했습니다.");
    }

    return response.data;
  }

  async createProduct(
    companyId: number,
    request: CreateProductRequest,
    thumbnailFile?: File | null,
    detailPageFiles?: File[] | null,
  ): Promise<ApiResponse<ProductDto>> {
    const formData = new FormData();
    formData.append(
      "request",
      new Blob([JSON.stringify(request)], { type: "application/json" }),
    );

    if (thumbnailFile) {
      formData.append("thumbnailFile", thumbnailFile);
    }

    if (detailPageFiles && detailPageFiles.length > 0) {
      detailPageFiles.forEach((file) => {
        formData.append("detailPageFiles", file);
      });
    }

    const response = await httpClient.post<ApiResponse<ProductDto>>(
      NEW_API_ENDPOINTS.companyProductController.POST(companyId),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상품을 생성하지 못했습니다.");
    }

    return response.data;
  }

  async updateProduct(
    companyId: number,
    productId: number,
    request: UpdateProductRequest,
    thumbnailFile?: File | null,
    detailPageFiles?: File[] | null,
  ): Promise<ApiResponse<ProductDto>> {
    const formData = new FormData();
    formData.append(
      "request",
      new Blob([JSON.stringify(request)], { type: "application/json" }),
    );

    if (thumbnailFile) {
      formData.append("thumbnailFile", thumbnailFile);
    }

    if (detailPageFiles && detailPageFiles.length > 0) {
      detailPageFiles.forEach((file) => {
        formData.append("detailPageFiles", file);
      });
    }

    const response = await httpClient.put<ApiResponse<ProductDto>>(
      NEW_API_ENDPOINTS.companyProductController.PUT(companyId, productId),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상품을 수정하지 못했습니다.");
    }

    return response.data;
  }

  /**
   * 상품 삭제
   */
  async deleteProduct(companyId: number, productId: number): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companyProductController.DELETE(companyId, productId),
    );

    if (response.data?.errorCode) {
      throw new Error(response.data.errorMsg || "상품을 삭제하지 못했습니다.");
    }

    return response.data;
  }

  /**
   * 상품에 옵션 연결
   */
  async linkProductOptions(
    companyId: number,
    productId: number,
    request: LinkProductOptionsRequest,
  ): Promise<ApiResponse<ProductDto>> {
    const response = await httpClient.put<ApiResponse<ProductDto>>(
      NEW_API_ENDPOINTS.companyProductController.LINK_OPTIONS(companyId, productId),
      request,
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "옵션을 연결하지 못했습니다.");
    }

    return response.data;
  }

  async unlinkProductOptions(
    companyId: number,
    productId: number,
    productOptionId: number,
  ): Promise<ApiResponse<ProductDto>> {
    const response = await httpClient.delete<ApiResponse<ProductDto>>(
      NEW_API_ENDPOINTS.companyProductController.UNLINK_OPTION(companyId, productId, productOptionId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "옵션을 연결해제 하지 못했습니다.");
    }

    return response.data;
  }

  async getDetailPages(
    companyId: number,
    productId: number,
  ): Promise<ApiResponse<DetailPageDto[]>> {
    const response = await httpClient.get<ApiResponse<DetailPageDto[]>>(
      NEW_API_ENDPOINTS.companyProductController.DETAIL_PAGES(companyId, productId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상세정보 이미지를 찾지 못했습니다.");
    }

    return response.data;
  }

  async addDetailPages(
    companyId: number,
    productId: number,
    files: File[],
  ): Promise<ApiResponse<DetailPageDto>> {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append("files", file);
    });

    const response = await httpClient.post<ApiResponse<DetailPageDto>>(
      NEW_API_ENDPOINTS.companyProductController.DETAIL_PAGES(companyId, productId),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상세페이지를 추가하지 못했습니다.");
    }

    return response.data;
  }

  async insertDetailPageAt(
    companyId: number,
    productId: number,
    file: File,
    afterDetailPageId: number,
  ): Promise<ApiResponse<DetailPageDto[]>> {
    const formData = new FormData();

    formData.append("file", file);
    formData.append("afterDetailPageId", afterDetailPageId.toString());

    const response = await httpClient.post<ApiResponse<DetailPageDto[]>>(
      NEW_API_ENDPOINTS.companyProductController.DETAIL_PAGES_INSERT(companyId, productId),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상세이미지를 대체시키지 못했습니다.");
    }

    return response.data;
  }

  async replaceDetailPages(
    companyId: number,
    productId: number,
    files: File[],
  ): Promise<ApiResponse<DetailPageDto[]>> {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append("files", file);
    });

    const response = await httpClient.put<ApiResponse<DetailPageDto[]>>(
      NEW_API_ENDPOINTS.companyProductController.DETAIL_PAGES(companyId, productId),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상세이미지를 대체시키지 못했습니다.");
    }

    return response.data;
  }

  async updateDetailPageOrders(
    companyId: number,
    productId: number,
    detailPageIds: number[],
  ): Promise<ApiResponse<DetailPageDto[]>> {
    const response = await httpClient.patch<ApiResponse<DetailPageDto[]>>(
      NEW_API_ENDPOINTS.companyProductController.DETAIL_PAGES_ORDER(companyId, productId),
      detailPageIds,
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상세이미지 순서를 변경하지 못했습니다.");
    }

    return response.data;
  }

  async deleteDetailPage(
    companyId: number,
    productId: number,
    detailPageId: number,
  ): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companyProductController.DETAIL_PAGES_DELETE(companyId, productId, detailPageId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "해당 상세이미지를 삭제하지 못했습니다.");
    }

    return response.data;
  }

  async deleteAllDetailPages(
    companyId: number,
    productId: number,
  ): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companyProductController.DETAIL_PAGES(companyId, productId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "해당 상품의 모든 상세이미지를 삭제하지 못했습니다.");
    }

    return response.data;
  }

  /**
   * 상품에서 개별 옵션 삭제
   */
  async deleteProductOption(
    companyId: number,
    productId: number,
    productOptionId: number,
  ): Promise<void> {
    const response = await httpClient.delete(
      NEW_API_ENDPOINTS.companyProductController.DELETE_OPTION(companyId, productId, productOptionId),
    );

    if (response.data?.errorCode) {
      throw new Error(response.data.errorMsg || "옵션을 삭제하지 못했습니다.");
    }
  }

  // ========== Category CRUD ==========

  /**
   * 카테고리 목록 조회
   */
  async getCategories(companyId: number): Promise<ApiResponseProductCategoryEntityList> {
    const response = await httpClient.get<ApiResponseProductCategoryEntityList>(
      NEW_API_ENDPOINTS.companyCategoryController.GET(companyId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "카테고리를 가져오지 못했습니다.");
    }

    return response.data;
  }

  /**
   * 카테고리 상세 조회
   */
  async getCategoryDetail(
    companyId: number,
    categoryId: number,
  ): Promise<ApiResponseProductCategoryEntity> {
    const response = await httpClient.get<ApiResponseProductCategoryEntity>(
      NEW_API_ENDPOINTS.companyCategoryController.GET_DETAIL(companyId, categoryId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "카테고리 상세 정보를 가져오지 못했습니다.");
    }

    return response.data;
  }

  /**
   * 카테고리 생성
   */
  async createCategory(
    companyId: number,
    request: CreateCategoryRequest,
  ): Promise<ApiResponseProductCategoryEntity> {
    const response = await httpClient.post<ApiResponseProductCategoryEntity>(
      NEW_API_ENDPOINTS.companyCategoryController.POST(companyId),
      request,
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "카테고리를 생성하지 못했습니다.");
    }

    return response.data;
  }

  /**
   * 카테고리 수정
   */
  async updateCategory(
    companyId: number,
    categoryId: number,
    request: UpdateCategoryRequest,
  ): Promise<ApiResponseProductCategoryEntity> {
    const response = await httpClient.put<ApiResponseProductCategoryEntity>(
      NEW_API_ENDPOINTS.companyCategoryController.PUT(companyId, categoryId),
      request,
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "카테고리를 수정하지 못했습니다.");
    }

    return response.data;
  }

  /**
   * 카테고리 삭제
   */
  async deleteCategory(companyId: number, categoryId: number): Promise<void> {
    const response = await httpClient.delete(
      NEW_API_ENDPOINTS.companyCategoryController.DELETE(companyId, categoryId),
    );

    if (response.data?.errorCode) {
      throw new Error(response.data.errorMsg || "카테고리를 삭제하지 못했습니다.");
    }
  }
}

export const companyProductApiService = new CompanyProductApiService();
